import React, { useEffect, useState, useRef } from "react";
import { FaTrash, FaInfoCircle } from "react-icons/fa";
import { Table, Form } from 'react-bootstrap';
import {
  GetAllItemsAssociatedWithSelectionGroup,
  UpdateItemSelectionGroupMinVariance,
  UpdateItemSelectionGroupMaxVariance,
  UpdateItemSelectionGroupMinSelections,
  UpdateItemSelectionGroupMaxSelections,
} from '../../../../../../../../crud/menu_api/selection_groups';
import { toast } from 'react-toastify';
import RemoveGroupFromItemModal from "./components/RemoveGroupFromItemModal/RemoveGroupFromItemModal";
import AddGroupToItemModal from "./components/AddGroupToItemModal/AddGroupToItemModal";
import './ItemGroupTable.scss';

interface SelectionGroupType {
  readonly selection_group_id: number;
  selection_group_name: string;
  selection_group_description?: string;
  local_id: number;
  restaurant_id?: number;
  items_affected?: { item_id: number; item_name: string; }[];
  items_contained?: { item_id: number; item_name: string; }[];
}

interface ItemSelectionType {
  item_id: number;
  selection_group_id: number;
  item_name: string;
  item_label: string;
  min_variance: number;
  max_variance: number;
  max_selections: number;
  min_selections: number;
  local_id: number;
  is_price_modifier: boolean;
}

interface ItemGroupsTableProps {
  sg: SelectionGroupType;
  /**
   * Optional registration callback: if provided, the component will not render its internal
   * “Agregar ítem base” button and instead expose a function so a parent can trigger the add modal.
   */
  registerAddHandler?: (openFn: () => void) => void;
}

const ItemGroupsTable: React.FC<ItemGroupsTableProps> = ({ sg, registerAddHandler }) => {
  const [items, setItems] = useState<ItemSelectionType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedItemSelection, setSelectedItemSelection] = useState<ItemSelectionType | null>(null);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const inputRefs = useRef<{[key: string]: HTMLInputElement | null}>({});

  // Tooltip content
  const tooltips: { [key: string]: string } = {
    variance_min: 'Diversidad mínima obligatoria. Si pones 2, el cliente debe elegir al menos 2 sabores distintos (no pueden ser todas iguales).',
    variance_max: 'Máximo de sabores distintos permitidos. Si pones 2, el cliente solo puede elegir hasta 2 tipos diferentes.',
    min_selections: 'Mínimo de unidades que el cliente DEBE elegir de este grupo. Ej: Pizza con 2 mitades = Min 2.',
    max_selections: 'Máximo de unidades que el cliente PUEDE elegir de este grupo. Ej: Extras opcionales = Max 3.',
  };

  useEffect(() => {
    setLoading(true);
    GetAllItemsAssociatedWithSelectionGroup(sg.selection_group_id)
      .then((response) => {
        setItems(response.data);
      })
      .catch((error) => {
        console.error('Error fetching items associated with selection group', error);
        toast.error('Error al obtener los ítems asociados al grupo de selección');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [sg.selection_group_id]);

  // Expose an external opener when requested
  useEffect(() => {
    if (registerAddHandler) {
      registerAddHandler(() => setShowAddModal(true));
    }
  }, [registerAddHandler]);

  /**
   * Mapeo de campos a sus respectivas funciones de actualización de la API.
   */
  const fieldToApiFunction: {
    [key: string]: (
      item_id: number,
      selection_group_id: number,
      value: number
    ) => Promise<any>;
  } = {
    min_variance: UpdateItemSelectionGroupMinVariance,
    max_variance: UpdateItemSelectionGroupMaxVariance,
    min_selections: UpdateItemSelectionGroupMinSelections,
    max_selections: UpdateItemSelectionGroupMaxSelections,
  };

  /**
   * Update local state only (for responsive UI while typing)
   */
  const handleLocalUpdate = (itemId: number, field: string, value: number) => {
    const updatedItems = items.map((i) => {
      if (i.item_id === itemId) {
        return { ...i, [field]: value };
      }
      return i;
    });
    setItems(updatedItems);
  };

  /**
   * Confirm update - calls API and handles errors
   */
  const handleConfirmUpdate = async (
    itemId: number,
    field: 'min_variance' | 'max_variance' | 'min_selections' | 'max_selections',
    value: number
  ) => {
    const item = items.find(i => i.item_id === itemId);
    if (!item) return;

    const currentValue = item[field];
    if (currentValue === value) return; // No change

    // Ensure non-negative
    const safeValue = Math.max(0, value);
    if (safeValue !== value) {
      handleLocalUpdate(itemId, field, safeValue);
    }

    try {
      const apiFunction = fieldToApiFunction[field];
      if (apiFunction) {
        await apiFunction(itemId, sg.selection_group_id, safeValue);
        toast.success('Actualizado con éxito.');
      } else {
        throw new Error(`No existe una función API para el campo ${field}`);
      }
    } catch (error: any) {
      console.error(`Error actualizando ${field}`, error);
      toast.error('Error al actualizar.');
      // Revert on error
      handleLocalUpdate(itemId, field, currentValue);
    }
  };

  /**
   * Render an input field with small arrow buttons for numeric values
   */
  const renderSelectionInput = (
    value: number,
    itemId: number,
    field: 'min_variance' | 'max_variance' | 'min_selections' | 'max_selections',
    label: string
  ) => (
    <div className="selection-input-container">
      <Form.Control
        ref={(ref: HTMLInputElement | null) => {
          inputRefs.current[`${field}_${itemId}`] = ref;
        }}
        type="number"
        min="0"
        value={value}
        onChange={(e) => handleLocalUpdate(itemId, field, Number(e.target.value))}
        onBlur={(e) => handleConfirmUpdate(itemId, field, Number(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleConfirmUpdate(itemId, field, Number((e.target as HTMLInputElement).value));
            (e.target as HTMLInputElement).blur();
          }
        }}
        className="selection-input"
        title={`${label} - Presiona Enter o sal del campo para guardar`}
      />
      <div className="selection-arrows">
        <button
          type="button"
          className="arrow-btn arrow-up"
          onClick={() => {
            const newValue = value + 1;
            handleLocalUpdate(itemId, field, newValue);
            handleConfirmUpdate(itemId, field, newValue);
          }}
        >
          ▲
        </button>
        <button
          type="button"
          className="arrow-btn arrow-down"
          onClick={() => {
            const newValue = Math.max(0, value - 1);
            handleLocalUpdate(itemId, field, newValue);
            handleConfirmUpdate(itemId, field, newValue);
          }}
          disabled={value === 0}
        >
          ▼
        </button>
      </div>
    </div>
  );

  /**
   * Función para manejar la eliminación de un ItemSelection.
   * @param itemSelection - El ítem seleccionado para eliminar.
   */
  const handleDeleteItemSelection = (itemSelection: ItemSelectionType) => {
    setSelectedItemSelection(itemSelection);
    setShowDeleteModal(true);
    // Aquí típicamente enviarías la solicitud de eliminación al backend a través del modal
  };

  if (loading) {
    return (
      <div className="loading-container">
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        <p>Cargando productos vinculados...</p>
      </div>
    );
  }

  const renderHeaderWithTooltip = (label: string, tooltipKey: string) => (
    <th className="header-with-tooltip">
      <div className="header-content">
        <span>{label}</span>
        <button 
          type="button" 
          className="tooltip-trigger"
          onClick={() => setActiveTooltip(activeTooltip === tooltipKey ? null : tooltipKey)}
          aria-label={`Información sobre ${label}`}
        >
          <FaInfoCircle />
        </button>
        {activeTooltip === tooltipKey && (
          <div className="tooltip-popup">
            <p>{tooltips[tooltipKey]}</p>
          </div>
        )}
      </div>
    </th>
  );

  return (
    <>
      {/* Click outside to close tooltip */}
      {activeTooltip && (
        <div className="tooltip-backdrop" onClick={() => setActiveTooltip(null)} />
      )}
      
      <Table responsive className="item-groups-configuration-table">
        <thead>
          <tr>
            <th className="item-name-col">Ítem</th>
            {renderHeaderWithTooltip('Variancia Mín', 'variance_min')}
            {renderHeaderWithTooltip('Variancia Máx', 'variance_max')}
            {renderHeaderWithTooltip('Mín Selecciones', 'min_selections')}
            {renderHeaderWithTooltip('Máx Selecciones', 'max_selections')}
            <th className="actions-col"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={item.item_id} className={index % 2 === 0 ? 'even' : 'odd'}>
              <td className="item-name-cell">
                <div className="item-info">
                  <strong className="item-name">{item.item_name}</strong>
                </div>
              </td>
              <td className="input-cell">
                {renderSelectionInput(item.min_variance, item.item_id, 'min_variance', 'Variancia mínima')}
              </td>
              <td className="input-cell">
                {renderSelectionInput(item.max_variance, item.item_id, 'max_variance', 'Variancia máxima')}
              </td>
              <td className="input-cell">
                {renderSelectionInput(item.min_selections, item.item_id, 'min_selections', 'Selecciones mínimas')}
              </td>
              <td className="input-cell">
                {renderSelectionInput(item.max_selections, item.item_id, 'max_selections', 'Selecciones máximas')}
              </td>
              <td className="actions-cell">
                <button
                  type="button"
                  className="delete-item-btn"
                  onClick={() => handleDeleteItemSelection(item)}
                  aria-label="Eliminar relación"
                >
                  <FaTrash />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Modal para Eliminar ItemSelection */}
      {showDeleteModal && selectedItemSelection && (
        <RemoveGroupFromItemModal
          show={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          sg={sg}
          itemSelection={selectedItemSelection}
          setItemSelections={setItems}
          itemSelections={items}
        />
      )}

      {/* Modal para Agregar ItemSelection */}
      {showAddModal && (
        <AddGroupToItemModal
          show={showAddModal}
          handleClose={() => setShowAddModal(false)}
          sg={sg}
          setItemSelections={setItems}
          itemSelections={items}
        />
      )}
    </>
  );
};

export default ItemGroupsTable;
